import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  useShadowRoot,
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
} from '@backstage/plugin-techdocs-react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';

import { oneadvancedPlugin } from '../../plugin';

const EXPAND_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">' +
  '<path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>' +
  '</svg>';

// data-* attribute stamped on each element BEFORE any DOM move so that the
// MutationObserver re-trigger caused by appendChild is a no-op.
const PROCESSED_ATTR = 'data-oa-image-expand';

// Minimum rendered dimension (px) before we attach expand behaviour.
const MIN_SIZE_PX = 80;

type ExpandTarget =
  | { kind: 'img'; src: string; alt: string }
  | { kind: 'svg'; html: string };

function isBigEnough(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width >= MIN_SIZE_PX || r.height >= MIN_SIZE_PX;
}

// Shared button styles — identical to MermaidExpandAddon.
function applyBtnStyles(btn: HTMLButtonElement, isDark: boolean) {
  Object.assign(btn.style, {
    position: 'absolute',
    top: '6px',
    right: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: '0',
    background: isDark ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.80)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'}`,
    borderRadius: '4px',
    cursor: 'pointer',
    color: isDark ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0.5)',
    opacity: '0.4',
    transition: 'opacity 0.2s, box-shadow 0.2s',
    zIndex: '10',
  });
}

// ---------------------------------------------------------------------------
// attachImgExpandButton
//
// Wraps the <img> in a <span> so an absolutely-positioned button can sit
// over its top-right corner — exactly like MermaidExpandAddon.
//
// Layout fix: we measure el.offsetWidth BEFORE wrapping and set that as an
// explicit pixel width on the wrapper. This breaks the circular CSS
// dependency (img width:100% inside an unsized inline-block) that caused the
// image to collapse to zero.
//
// Race-condition fix: PROCESSED_ATTR is stamped on the img BEFORE any DOM
// move, so the MutationObserver re-trigger sees it and bails immediately.
// ---------------------------------------------------------------------------
function attachImgExpandButton(
  el: HTMLImageElement,
  onExpand: (target: ExpandTarget) => void,
  paletteMode: 'light' | 'dark',
): () => void {
  if (!el.parentNode) return () => { /* no-op */ };

  const isDark = paletteMode === 'dark';

  // Stamp FIRST — any observer re-trigger will see this and skip.
  el.setAttribute(PROCESSED_ATTR, '1');

  // Measure the image's rendered width BEFORE moving it into the wrapper.
  // This explicit pixel width breaks the circular 'width:100% inside
  // inline-block' dependency that collapses the image to zero.
  const renderedWidth = el.offsetWidth;

  const wrapper = document.createElement('span');
  wrapper.className = 'oa-image-expand-wrapper';
  wrapper.setAttribute(PROCESSED_ATTR, '1');
  Object.assign(wrapper.style, {
    position: 'relative',
    display: 'inline-block',
    width: renderedWidth > 0 ? `${renderedWidth}px` : 'auto',
    maxWidth: '100%',
    lineHeight: '0',
  });

  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);

  const btn = document.createElement('button');
  btn.className = 'oa-image-expand-btn';
  btn.setAttribute('aria-label', 'Expand image');
  btn.title = 'Expand image';
  btn.innerHTML = EXPAND_ICON_SVG;
  applyBtnStyles(btn, isDark);

  const onEnter = () => {
    btn.style.opacity = '1';
    btn.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
  };
  const onLeave = () => {
    btn.style.opacity = '0.4';
    btn.style.boxShadow = 'none';
  };
  const onClick = (e: Event) => {
    e.stopPropagation();
    onExpand({ kind: 'img', src: el.src, alt: el.alt });
  };

  wrapper.addEventListener('mouseenter', onEnter);
  wrapper.addEventListener('mouseleave', onLeave);
  btn.addEventListener('click', onClick);
  wrapper.appendChild(btn);

  return () => {
    wrapper.removeEventListener('mouseenter', onEnter);
    wrapper.removeEventListener('mouseleave', onLeave);
    btn.removeEventListener('click', onClick);
    if (wrapper.parentNode) wrapper.parentNode.insertBefore(el, wrapper);
    wrapper.remove();
    el.removeAttribute(PROCESSED_ATTR);
  };
}

// ---------------------------------------------------------------------------
// attachSvgExpandButton
//
// Standalone <svg> elements can have an HTML button child positioned
// absolutely — identical technique to MermaidExpandAddon.
// ---------------------------------------------------------------------------
function attachSvgExpandButton(
  el: SVGElement,
  onExpand: (target: ExpandTarget) => void,
  paletteMode: 'light' | 'dark',
): () => void {
  el.setAttribute(PROCESSED_ATTR, '1');

  const isDark = paletteMode === 'dark';
  const container = el as unknown as HTMLElement;
  container.style.position = 'relative';

  const btn = document.createElement('button');
  btn.className = 'oa-image-expand-btn';
  btn.setAttribute('aria-label', 'Expand image');
  btn.title = 'Expand image';
  btn.innerHTML = EXPAND_ICON_SVG;
  applyBtnStyles(btn, isDark);

  const onEnter = () => {
    btn.style.opacity = '1';
    btn.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
  };
  const onLeave = () => {
    btn.style.opacity = '0.4';
    btn.style.boxShadow = 'none';
  };
  const onClick = (e: Event) => {
    e.stopPropagation();
    const responsive = el.outerHTML
      .replace(/\s+width="[^"]*"/, '')
      .replace(/\s+height="[^"]*"/, '')
      .replace(/(<svg\b[^>]*)\sstyle="([^"]*)"/, (_m: string, tag: string, style: string) => {
        const cleaned = style
          .split(';')
          .map((s: string) => s.trim())
          .filter((s: string) => s && !/^max-width\s*:/.test(s))
          .join('; ');
        return cleaned ? `${tag} style="${cleaned}"` : tag;
      });
    onExpand({ kind: 'svg', html: responsive });
  };

  container.addEventListener('mouseenter', onEnter);
  container.addEventListener('mouseleave', onLeave);
  btn.addEventListener('click', onClick);
  container.appendChild(btn);

  return () => {
    container.removeEventListener('mouseenter', onEnter);
    container.removeEventListener('mouseleave', onLeave);
    btn.removeEventListener('click', onClick);
    if (btn.parentNode) btn.remove();
    el.removeAttribute(PROCESSED_ATTR);
  };
}

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;

function ImageExpandAddonComponent() {
  const [target, setTarget] = useState<ExpandTarget | null>(null);
  const [zoom, setZoom] = useState(1);
  const shadowRoot = useShadowRoot();
  const { mode: paletteMode } = useTheme().palette;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const scrollCentreRef = useRef({ x: 0.5, y: 0.5 });

  const changeZoom = useCallback((updater: number | ((z: number) => number)) => {
    const el = contentRef.current;
    if (el) {
      scrollCentreRef.current = {
        x: el.scrollWidth > el.clientWidth
          ? (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth
          : 0.5,
        y: el.scrollHeight > el.clientHeight
          ? (el.scrollTop + el.clientHeight / 2) / el.scrollHeight
          : 0.5,
      };
    }
    setZoom(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return Math.round(Math.min(Math.max(next, ZOOM_MIN), ZOOM_MAX) * 10) / 10;
    });
  }, []);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const { x, y } = scrollCentreRef.current;
    el.scrollLeft = x * el.scrollWidth - el.clientWidth / 2;
    el.scrollTop = y * el.scrollHeight - el.clientHeight / 2;
  }, [zoom]);

  useEffect(() => {
    if (!target) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      changeZoom(z => z + (e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP));
    };
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => document.removeEventListener('wheel', onWheel);
  }, [target, changeZoom]);

  const onExpandRef = useRef<(t: ExpandTarget) => void>(null!);
  onExpandRef.current = (t: ExpandTarget) => {
    setTarget(t);
    setZoom(1);
  };

  useEffect(() => {
    if (!shadowRoot) return undefined;

    const cleanups = new Map<Element, () => void>();

    const processAll = () => {
      // <img> elements — wrap with a measured-width span for the button overlay
      shadowRoot.querySelectorAll<HTMLImageElement>('img').forEach(el => {
        if (el.hasAttribute(PROCESSED_ATTR)) return;
        if (el.closest('.mermaid')) return;
        if (el.closest('.oa-image-expand-wrapper')) return;
        if (!isBigEnough(el)) return;

        const cleanup = attachImgExpandButton(
          el,
          t => onExpandRef.current(t),
          paletteMode,
        );
        cleanups.set(el, cleanup);
      });

      // Standalone <svg> elements — append button directly (same as MermaidExpandAddon)
      shadowRoot.querySelectorAll<SVGElement>('svg').forEach(el => {
        if (el.hasAttribute(PROCESSED_ATTR)) return;
        if (el.closest('.mermaid')) return;
        if (el.closest('.oa-image-expand-btn')) return;
        if (!isBigEnough(el)) return;

        const cleanup = attachSvgExpandButton(
          el,
          t => onExpandRef.current(t),
          paletteMode,
        );
        cleanups.set(el, cleanup);
      });
    };

    const observer = new MutationObserver(processAll);
    observer.observe(shadowRoot, { childList: true, subtree: true });
    processAll();

    return () => {
      observer.disconnect();
      cleanups.forEach(fn => fn());
      cleanups.clear();
    };
  }, [shadowRoot, paletteMode]);

  const handleClose = () => {
    setTarget(null);
    setZoom(1);
    scrollCentreRef.current = { x: 0.5, y: 0.5 };
  };

  if (target === null) return null;

  const title = target.kind === 'img' ? (target.alt || 'Image') : 'Image';

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: '92vw',
          maxWidth: '92vw',
          height: '88vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h6" component="span" sx={{ mr: 1 }}>
            {title}
          </Typography>
          <Tooltip title="Zoom in (+10%)">
            <IconButton size="small" onClick={() => changeZoom(z => z + ZOOM_STEP)}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <IconButton size="small" onClick={() => changeZoom(1)}>
              <ZoomOutMapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out (−10%)">
            <IconButton size="small" onClick={() => changeZoom(z => z - ZOOM_STEP)}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, mr: 2 }}>
            {Math.round(zoom * 100)}%
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Ctrl+scroll to zoom
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent ref={contentRef} sx={{ overflow: 'auto', p: 3 }}>
        <Box
          sx={{
            width: `${zoom * 100}%`,
            minWidth: `${zoom * 100}%`,
            lineHeight: 0,
            '& img': { width: '100%', height: 'auto', display: 'block' },
            '& svg': {
              width: '100% !important',
              height: 'auto !important',
              maxWidth: 'none !important',
              display: 'block',
            },
          }}
        >
          {target.kind === 'img' ? (
            <img src={target.src} alt={target.alt} />
          ) : (
            // SVG originates from TechDocs shadow DOM — same trust level as
            // the dangerouslySetInnerHTML already used by the shadow DOM itself.
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: target.html }} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export const ImageExpand = oneadvancedPlugin.provide(
  createTechDocsAddonExtension({
    name: 'ImageExpand',
    location: TechDocsAddonLocations.Content,
    component: ImageExpandAddonComponent,
  }),
);
